/*
 * pure shaping for the Reports tab: the status and table envelopes, cell text by column kind, the
 * file and sheet names, and the "Recalculate now" outcomes. No I/O (adapters.test.js).
 */
import { EM_DASH, TOTALS_ROUNDING_NOTE, fmt, fmt_count } from '../format';
import { with_ist } from '../shared/ist';

// Mirrors of the server's reports.FileName rule and draft column (pinned to reports_enums.json in tests).
export const FILE_PREFIX = 'teacher_payments_';
export const DRAFT_SUFFIX = '_DRAFT';
export const ISSUES_FILE_PREFIX = 'teacher_payments_to_be_reviewed_';
export const REPORT_STATUS_COLUMN = 'report_status';
export const REPORT_STATUS_LABEL = 'Report status';

const list = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (typeof v === 'string' ? v : '');

const draft_view = (d) => ({
  is_draft: Boolean(d && d.is_draft),
  label: str(d && d.label)
});

export const blockers_view = (rows) =>
  list(rows).map((b) => ({
    teacher_uuid: str(b.teacher_uuid),
    teacher_name: str(b.teacher_name),
    lifecycle: str(b.lifecycle),
    needs_recalc: Boolean(b.needs_recalc),
    compute_error: str(b.compute_error)
  }));

export const status_view = (data) => {
  const d = data || {};
  return {
    pay_month: str(d.pay_month),
    month_label: str(d.month_label),
    draft: draft_view(d.draft),
    blocked: Boolean(d.blocked),
    blockers: blockers_view(d.blockers),
    reports: list(d.reports).map((r) => ({
      key: str(r.key),
      title: str(r.title),
      description: str(r.description)
    })),
    teacher_count: Number(d.teacher_count) || 0
  };
};

// OPEN -> "Open", NO_MONTH_ROW -> "No month row": the UI shows sentence case, never the enum.
export const lifecycle_label = (lifecycle) => {
  const s = str(lifecycle).replace(/_/g, ' ').toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

// Why a teacher blocks the month: the engine's error, else the stale flag.
export const blocker_reason = (b) =>
  b.compute_error || 'Marked for recalculation: the stored amounts are out of date.';

// ---- the report table (preview panel and XLSX) ----

export const table_view = (data) => {
  const d = data || {};
  return {
    report: str(d.report),
    title: str(d.title),
    pay_month: str(d.pay_month),
    draft: draft_view(d.draft),
    generated_at_ist: str(d.generated_at_ist),
    columns: list(d.columns).map((c) => ({
      key: str(c.key),
      label: str(c.label),
      kind: str(c.kind)
    })),
    rows: list(d.rows),
    total_rows: Number(d.total_rows) || 0,
    totals: d.totals && typeof d.totals === 'object' ? d.totals : {}
  };
};

const is_blank = (v) => v === null || v === undefined || v === '';

// What the preview shows in a cell; the files keep the raw JSON value.
export const cell_text = (kind, v) => {
  if (is_blank(v)) return EM_DASH;
  switch (kind) {
    case 'money2':
      return fmt(v, 2);
    case 'rupees':
      return fmt(v);
    case 'int':
      return fmt_count(v);
    case 'bool':
      return v ? 'Yes' : 'No';
    // The server sends IST datetimes; the label travels with the value on phone cards too.
    case 'datetime':
      return with_ist(String(v));
    default:
      return String(v);
  }
};

// The additional-payments totals are not columns, so they carry their own names.
const TOTAL_LABELS = {
  net_total: 'Net (active entries)',
  removed_total: 'Removed by Ops',
  waived_total: 'Carry-forwards waived'
};

export const totals_view = (table) =>
  Object.keys(table.totals).map((key) => {
    const col = table.columns.find((c) => c.key === key);
    return {
      key,
      label: TOTAL_LABELS[key] || (col && col.label) || key,
      text: cell_text(col && col.kind === 'rupees' ? 'rupees' : 'money2', table.totals[key])
    };
  });

// ---- file and sheet names ----

// teacher_payments_<key>_<YYYY-MM>[_DRAFT].<ext>, the server's rule, for when its header is unreadable.
export const report_file_name = (key, pay_month, is_draft, ext) =>
  `${FILE_PREFIX}${key}_${pay_month}${is_draft ? DRAFT_SUFFIX : ''}${ext ? `.${ext}` : ''}`;

export const issues_file_name = (pay_month) =>
  `${ISSUES_FILE_PREFIX}${String(pay_month || '').slice(0, 7)}.csv`;

// Excel refuses a sheet name over 31 characters or holding : \ / ? * [ ] (the write throws).
const SHEET_NAME_MAX = 31;
export const sheet_name = (title, is_draft) =>
  `${is_draft ? 'DRAFT - ' : ''}${str(title)}`
    .replace(/[:\\/?*[\]]/g, ' ')
    .slice(0, SHEET_NAME_MAX)
    .trim();

// The XLSX is the full /reports/data JSON as a sheet: same columns and order, money as numbers, and
// on a draft month the report_status column first on every row. Text goes in unescaped: the library
// writes strings as text cells, which Excel never evaluates.
export const xlsx_sheet = (table) => {
  const { is_draft, label } = table.draft;
  const columns = table.columns.map((c) => ({ label: c.label, value: c.key }));
  const out_columns = is_draft
    ? [{ label: REPORT_STATUS_LABEL, value: REPORT_STATUS_COLUMN }].concat(columns)
    : columns;
  const rows = is_draft
    ? table.rows.map((r) => ({ [REPORT_STATUS_COLUMN]: label, ...r }))
    : table.rows;
  // addendum: matches the server CSV's final rounding-note row.
  const note_row = { [out_columns[0].value]: TOTALS_ROUNDING_NOTE };
  return {
    file_name: report_file_name(table.report, table.pay_month, is_draft, ''),
    sheet_name: sheet_name(table.title, is_draft),
    columns: out_columns,
    rows: rows.concat([note_row])
  };
};

// ---- "Recalculate now" ----

export const RECALC_DONE = 'done';
export const RECALC_SKIPPED = 'skipped';
export const RECALC_FAILED = 'failed';

// The engine skips a month that is not OPEN with developer text; Ops fix it by reopening.
const NOT_OPEN_RE = /month lifecycle is (\w+), not OPEN/;
const skip_text = (reason) => {
  const m = str(reason).match(NOT_OPEN_RE);
  if (!m) return 'Not recalculated.';
  const state = m[1] === 'CLOSED' ? 'closed' : 'not open';
  return `Not recalculated - this month is ${state}. Reopen it to recalculate.`;
};

export const recalc_outcome = (data) => {
  const r = (data && data.recompute) || {};
  if (str(r.error) || r.ok === false) {
    return {
      kind: RECALC_FAILED,
      text: `Failed: ${str(r.error) || 'the month could not be recalculated.'}`
    };
  }
  if (r.skipped) return { kind: RECALC_SKIPPED, text: skip_text(r.skip_reason) };
  // Only an explicit ok counts: an answer with no recompute block is not proof it ran.
  if (r.ok !== true) return recalc_failure('the server did not confirm the recalculation.');
  return { kind: RECALC_DONE, text: 'Recalculated' };
};

export const recalc_failure = (message) => ({
  kind: RECALC_FAILED,
  text: `Failed: ${message || 'the recalculation request did not go through.'}`
});

// ---- preview table arrangement (layout only: the files keep the server's column order) ----

// Always-visible money (Payable, Charged, Penalties); a report with none keeps its first money column.
const KEEP_MONEY = ['payable', 'charged', 'penalty_charged', 'penalties'];
const STATUS_KEYS = ['lifecycle', 'status', 'waiver_status', 'session_status'];
const MONEY_KINDS = ['money2', 'rupees'];
const RIGHT_KINDS = ['money2', 'rupees', 'int', 'decimal', 'datetime'];
const DIGITS = /^\d+$/;

// A uuid column: one unbroken value, shown truncated with the full value a tap away.
export const is_id_column = (c) => /_uuid$/.test(str(c && c.key));

// A text column whose every value is a plain number (penalty_id) reads as a number: right-aligned.
const digits_only = (key, rows) => {
  const values = list(rows)
    .map((r) => r && r[key])
    .filter((v) => !is_blank(v));
  return values.length > 0 && values.every((v) => DIGITS.test(String(v)));
};

/*
 * The preview's column order and roles: Teacher first, then Teacher ID, the always-visible money and
 * the status, then the rest in the server's order. Numbers and dates align right.
 */
export const preview_layout = (columns, rows) => {
  const cols = list(columns);
  const money = cols.filter((c) => MONEY_KINDS.indexOf(c.kind) !== -1);
  const kept = KEEP_MONEY.map((k) => money.find((c) => c.key === k)).filter(Boolean);
  const keep_money = kept.length ? kept : money.slice(0, 1);
  const status = cols.find((c) => STATUS_KEYS.indexOf(c.key) !== -1);
  const teacher = cols.find((c) => c.key === 'teacher_name');
  const teacher_id = cols.find((c) => c.key === 'teacher_uuid');
  const front = [teacher, teacher_id, ...keep_money, status].filter(Boolean);
  const ordered = front.concat(cols.filter((c) => front.indexOf(c) === -1));
  return ordered.map((c) => {
    let role;
    if (c === teacher) role = 'identifier';
    else if (keep_money.indexOf(c) !== -1) role = 'amount';
    else if (c === status) role = 'status';
    const right =
      RIGHT_KINDS.indexOf(c.kind) !== -1 || (c.kind === 'text' && digits_only(c.key, rows));
    return { ...c, role, align: right ? 'right' : undefined, is_id: is_id_column(c) };
  });
};

/*
 * The preview's four tracks (list-row pattern) from preview_layout's columns: who (teacher + ids),
 * details (every other text, count and date), the money ledger (total = the first always-visible
 * amount, the rest above it in server order) and the status. Every column lands in exactly one.
 */
export const preview_tracks = (laid) => {
  const cols = list(laid);
  const identifier = cols.find((c) => c.role === 'identifier') || null;
  const total = cols.find((c) => c.role === 'amount') || null;
  const status = cols.find((c) => c.role === 'status') || null;
  const rest = cols.filter((c) => c !== identifier && c !== total && c !== status);
  const ids = rest.filter((c) => c.is_id);
  const money = rest.filter((c) => !c.is_id && MONEY_KINDS.indexOf(c.kind) !== -1);
  const details = rest.filter((c) => ids.indexOf(c) === -1 && money.indexOf(c) === -1);
  // A report without a teacher column names its rows by the first detail.
  if (!identifier && details.length) {
    return { identifier: details[0], ids, details: details.slice(1), money, total, status };
  }
  return { identifier, ids, details, money, total, status };
};
